import { uniq } from 'lodash';
import themeService from '@/services/themeService';
import translate, { formatNumber } from '@/services/translate';
import globalEventBus from '@/services/globalEventBus';
import events from '@/domain/events/charts';
import { getColorsListByLength } from '@/domain/data/charts/chartColors';
import exponentialFilter,
{
  checkNumberIsValidForScientificNotation,
}
from '@/filters/exponentialFilter';
import getDataManager from './dataManagers';

const EDGE_MARGIN = 15;
const DEFAULT_DATETIME_EDGE_MARGIN_LEFT = 75;
const MINIMUM_DATETIME_EDGE_MARGIN_LEFT = 90;
const DEFAULT_DATETIME_EDGE_MARGIN_RIGHT = 40;
const X_DATA_MANAGER_DATETIME = 'dateTime';

let axisOffsets = [];
let axisSpacings = [];

const numberFormatterRule = number => (
  checkNumberIsValidForScientificNotation(number)
  ? exponentialFilter(number, 2)
  : formatNumber(number, { minimumFractionDigits: 1, maximumFractionDigits: number.length })
);

const getDisabledIcon = (tool, isPrinting) => {
  const icons = {
    [themeService.themes.DARK]: {
      grid: 'chart-grid-disabled-white',
      'multi-axis': 'chart-multi-disabled-white',
      outlier: 'chart-outlier-disabled-white',
      tooltip: 'chart-tooltip-disabled-white',
      smooth: 'chart-smooth-disabled-white',
    },
    [themeService.themes.LIGHT]: {
      grid: 'chart-grid-disabled',
      'multi-axis': 'chart-multi-disabled',
      outlier: 'chart-outlier-disabled',
      tooltip: 'chart-tooltip-disabled',
      smooth: 'chart-smooth-disabled',
    },
  };
  const theme = isPrinting ? themeService.themes.LIGHT : document.body.dataset.theme;
  return icons[theme][tool];
};

const createXAxisItem = (data, options) => ({
  type: 'category',
  axisTick: {
    alignWithLabel: true,
  },
  axisLabel: {
    lineHeight: 15,
  },
  splitLine: {
    show: options.showGrid,
    lineStyle: {
      color: themeService.getStyleVar('color-grid-x-axis'),
    },
  },
  axisLine: {
    onZero: false,
    lineStyle: {
      color: options.isPrinting
        ? themeService.getStyleVar('color-gray-medium-dark')
        : themeService.getFontColor(),
    },
  },
  data,
});

function getAxisValuesSpacing(greaterValue = 0) {
  const greaterValueCharacters = greaterValue.toString().length;
  const minSpacing = 75;
  const calculatedValue = greaterValueCharacters * 10;
  return calculatedValue > minSpacing ? calculatedValue : minSpacing;
}

function getYOffset(index, greaterValue) {
  const previousAxisOffset = axisOffsets[index - 1];
  const previousAxisSpacing = axisSpacings[index - 1];
  const offset = index < 2
    ? 0
    : previousAxisOffset + previousAxisSpacing;

  axisOffsets[index] = offset;
  axisSpacings[index] = getAxisValuesSpacing(greaterValue);
  return offset;
}

function generateYAxisItem(values, index, options) {
  let greaterValue = 0;
  for (let i = 0; i < values.length; i += 1) {
    greaterValue = Math.abs(values[i]) > greaterValue
      ? Math.abs(values[i])
      : greaterValue;
  }

  const formattedGreaterValue = numberFormatterRule(greaterValue);
  const name = `Y${index + 1}`;
  const TWENTY_PERCENT = 0.2;

  return {
    type: 'value',
    name,
    min: value => value.min - (TWENTY_PERCENT * Math.abs(value.min)),
    max: value => value.max + (TWENTY_PERCENT * Math.abs(value.max)),
    offset: getYOffset(index, formattedGreaterValue),
    axisLabel: {
      formatter: numberFormatterRule,
    },
    splitLine: {
      show: options.showGrid,
      lineStyle: {
        color: themeService.getStyleVar('color-grid-y-axis'),
      },
    },
    axisLine: {
      show: true,
      lineStyle: {
        color: options.isPrinting
          ? themeService.getStyleVar('color-gray-medium-dark')
          : themeService.getFontColor(),
      },
    },
  };
}

function generateXAxis(values, xDataManager, options) {
  if (options.startIndexFromOne) {
    values.unshift({ x: 0, y: null });
  }

  const xValues = values.map(({ x }) => x);

  const xValuesFormatted = uniq(xValues)
    .sort(xDataManager.sortComparer)
    .map(x => xDataManager.format(x, options.timeZone));

  return [createXAxisItem(xValuesFormatted, options)];
}

const getSeriesItem = (chartField, options, valuesFiltered, xDataManager) => ({
  name: chartField.name,
  type: options.chartType,
  smooth: options.smoothed,
  yAxisIndex: chartField.axis,
  showAllSymbol: false,
  data: (valuesFiltered || [])
    .map(({ x, y }) => [xDataManager.format(x, options.timeZone), y]),
});

const getSeriesAndAxis = (chartData, xDataManager, options) => {
  const series = [];
  const values = [];
  const yAxis = [];
  const yValues = [];
  let xAxis = [];
  let allYAxis = chartData.map(item => item.axis) || [];

  allYAxis = uniq(allYAxis).sort() || [0];
  allYAxis.forEach(() => {
    yValues.push([]);
  });

  for (let i = 0; i < chartData.length; i += 1) {
    const chartField = chartData[i];

    const valuesFilteredConverted = chartField.values
      .filter(Boolean)
      .map(({ x, y }) => {
        let valueToConvert = y;
        if (typeof y === 'string') {
          valueToConvert = y.replace(',', '.');
        }
        const numValue = Number(valueToConvert);
        return { x, y: numValue };
      });

    series.push(getSeriesItem(chartField, options, valuesFilteredConverted, xDataManager));

    if ((valuesFilteredConverted || []).length) {
      values.push(...valuesFilteredConverted);
    }

    xAxis = generateXAxis(values, xDataManager, options);

    const yMappedValues = chartField.values.map(({ y }) => {
      let valueToConvert = y;
      if (typeof y === 'string') {
        valueToConvert = y.replace(',', '.');
      }
      const numValue = Number(valueToConvert);
      return numValue;
    });
    yValues[chartField.axis || 0].push(yMappedValues);
  }

  allYAxis.forEach((item, index) => {
    yValues[index] = yValues[index].flat();
    yAxis.push(generateYAxisItem(yValues[index], index, options));
  });

  return {
    series,
    xAxis,
    yAxis: yAxis.length === 1 ? yAxis[0] : yAxis,
  };
};

function clearAxisInfo() {
  axisOffsets = [];
  axisSpacings = [];
}

function getGridRight() {
  if (axisSpacings.length === 1) return DEFAULT_DATETIME_EDGE_MARGIN_RIGHT;
  const greaterAxisOffset = Math.max(...axisOffsets);
  const lastAxisSpacing = axisSpacings[axisSpacings.length - 1];
  return greaterAxisOffset + lastAxisSpacing;
}

function getGridLeft(options) {
  const firstAxisSpacing = axisSpacings[0];
  const { xDataManager } = options;

  if (xDataManager === X_DATA_MANAGER_DATETIME
    && firstAxisSpacing < MINIMUM_DATETIME_EDGE_MARGIN_LEFT) {
    return DEFAULT_DATETIME_EDGE_MARGIN_LEFT;
  }

  return firstAxisSpacing - EDGE_MARGIN;
}

const getIconStyle = options => ({
  borderColor: options.isPrinting
    ? themeService.getStyleVar('color-gray-medium-dark')
    : themeService.getFontColor(),
});

const getMyOutlierTool = options => ({
  show: true,
  title: options.removeOutlier
    ? translate('e-charts.toolbox.feature.outlier.disable')
    : translate('e-charts.toolbox.feature.outlier.enable'),
  icon: options.removeOutlier
    ? 'image://static/imgs/chart-outlier.svg'
    : `image://static/imgs/${getDisabledIcon('outlier', options.isPrinting)}.svg`,
  onclick: () => {
    globalEventBus.emit(events.toggleOutlier);
  },
});

const getMySmoothTool = options => ({
  show: true,
  title: options.smoothed
    ? translate('e-charts.toolbox.feature.smooth.disable')
    : translate('e-charts.toolbox.feature.smooth.enable'),
  icon: options.smoothed
    ? 'image://static/imgs/chart-smooth.svg'
    : `image://static/imgs/${getDisabledIcon('smooth', options.isPrinting)}.svg`,
  onclick: () => {
    globalEventBus.emit(events.toggleSmooth, options.chartId);
  },
});

const getMyGridTool = options => ({
  show: true,
  title: options.showGrid
    ? translate('e-charts.toolbox.feature.grid.disable')
    : translate('e-charts.toolbox.feature.grid.enable'),
  icon: options.showGrid
    ? 'image://static/imgs/chart-grid.svg'
    : `image://static/imgs/${getDisabledIcon('grid', options.isPrinting)}.svg`,
  onclick: () => {
    globalEventBus.emit(events.toggleGrid, options.chartId);
  },
});

const getMyMultiAxisTool = options => ({
  show: true,
  title: options.enableMultiAxis
    ? translate('e-charts.toolbox.feature.multi-axis.disable')
    : translate('e-charts.toolbox.feature.multi-axis.enable'),
  icon: options.enableMultiAxis
    ? 'image://static/imgs/chart-multi.svg'
    : `image://static/imgs/${getDisabledIcon('multi-axis', options.isPrinting)}.svg`,
  onclick: () => {
    globalEventBus.emit(events.toggleMultiAxis);
  },
});

const getMyTooltipTool = options => ({
  show: true,
  title: options.showTooltip
    ? translate('e-charts.toolbox.feature.tooltip.disable')
    : translate('e-charts.toolbox.feature.tooltip.enable'),
  icon: options.showTooltip
    ? 'image://static/imgs/chart-tooltip.svg'
    : `image://static/imgs/${getDisabledIcon('tooltip', options.isPrinting)}.svg`,
  onclick: () => {
    globalEventBus.emit(events.toggleTooltip, options.chartId);
  },
});

const getMagicType = () => ({
  title: {
    line: translate('e-charts.toolbox.feature.magic-type.title.line'),
    bar: translate('e-charts.toolbox.feature.magic-type.title.bar'),
  },
  type: ['line', 'bar'],
});

const getRestore = () => ({
  title: translate('e-charts.toolbox.feature.restore.title'),
});

const getDataZoom = () => ({
  title: {
    zoom: translate('e-charts.toolbox.feature.data-zoom.title.zoom'),
    back: translate('e-charts.toolbox.feature.data-zoom.title.back'),
  },
});

const getOptionToContent = options => ((option) => {
  const tableHeaderStyle = 'word-break: break-word; white-space: normal; min-width: 10vw; max-width: 30vw;';
  const axisData = options.startIndexFromOne ? option.xAxis[0].data.slice(1) : option.xAxis[0].data;
  const { series } = option;
  const firstColumnTitle = translate(options.firstColumnTitle);
  const columns = series.map((serie) => {
    const { name } = serie;
    if ((name || '').length < 50) {
      return `<th style="${tableHeaderStyle}" title="${name}">${name}</th>`;
    }

    return `<th style="${tableHeaderStyle}" title="${name}">${name.slice(0, 50)}...</th>`;
  });
  const values = series.map(serie => (
    (serie.data || []).map(value => ([
      ...value,
    ]))
  ));
  const dateFilteredArray = [];
  const rowsValues = [];

  for (let i = 0; i < axisData.length; i += 1) {
    dateFilteredArray[i] = values
      .map(element => element.find(value => value[0] === axisData[i]));
  }

  for (let i = 0; i < dateFilteredArray.length; i += 1) {
    rowsValues[i] = dateFilteredArray[i].map((cartesianValues) => {
      if (!cartesianValues) {
        return '<td>--</td>';
      }
      const yValue = cartesianValues[1];
      return yValue === null
        ? '<td>*</td>'
        : `<td>${numberFormatterRule(yValue)}</td>`;
    });
  }

  let table = `
    <table border="1" cellpadding="10" style="width:100%;text-align:center;color:${themeService.getFontColor()};position:relative;border:1px solid ${themeService.getFontColor()};margin-bottom: 2px">
      <thead>
        <tr>
          <th>${firstColumnTitle}</th>
          ${columns.join(' ')}
        </tr>
      </thead>
      </tbody>
  `;

  for (let i = 0; i < rowsValues.length; i += 1) {
    table += `
      <tr>
        <td style="word-break:keep-all; white-space: nowrap">${axisData[i]}</td>
        ${rowsValues[i].join(' ')}
      </tr>
    `;
  }

  table += '</tbody></table>';

  if (options.removeOutlier) {
    table += `
      <div style="position: absolute;bottom: 5px;">
        ${translate('e-charts.toolbox.feature.data-view.outlier-legend')}
      </div>
    `;
  }

  return table;
});

const getDataView = options => ({
  title: translate('e-charts.toolbox.feature.data-view.title'),
  readOnly: true,
  lang: translate('e-charts.toolbox.feature.data-view.lang'),
  textColor: themeService.getFontColor(),
  backgroundColor: themeService.getStyleVar('color-background-light'),
  optionToContent: getOptionToContent(options),
  buttonColor: 'var(--color-secondary)',
  buttonTextColor: 'var(--color-white-100)',
});

function getToolboxConfig(options) {
  if (typeof options.toolbox === 'object') {
    return options.toolbox;
  }

  const iconStyle = getIconStyle(options);
  const myOutlierTool = getMyOutlierTool(options);
  const mySmoothTool = getMySmoothTool(options);
  const myGridTool = getMyGridTool(options);
  const myMultiAxisTool = getMyMultiAxisTool(options);
  const myTooltipTool = getMyTooltipTool(options);
  const dataView = getDataView(options);
  const magicType = getMagicType();
  const restore = getRestore();
  const dataZoom = getDataZoom();

  return {
    show: true,
    top: 5,
    right: 45,
    iconStyle,
    feature: {
      myOutlierTool,
      mySmoothTool,
      myGridTool,
      myMultiAxisTool,
      myTooltipTool,
      dataView,
      magicType,
      restore,
      dataZoom,
    },
  };
}

function getExtraOptions(options) {
  const extraOptions = {};

  if (options.toolbox) {
    extraOptions.toolbox = getToolboxConfig(options);
  }

  return extraOptions;
}

const tooltipLabelFormatter = (value) => {
  if (value.axisDimension === 'y') {
    return numberFormatterRule(value.value);
  }
  return value.value.replace('\n', ' ');
};

/**
 * Entidade onde os campos tem valores no formato ChartValue.
 * @typedef ChartValue
 * @see /packages/SigmaMappings/src/entities/chart/ChartValue.ts
 */

/**
 * Opções de geração.
 * @typedef {Object} GenerateOptions
 * @param {Boolean|Object} [toolbox] Adicionar toolbox. Se true, adiciona toolbox padrão.
 * Se objeto, adiciona o objeto passado como configuração da toolbox. Opcional, padrão undefined.
 * @param {String|DataManager} [xDataManager] String de apelido ou objeto de gerenciamento de dados
 * do eixo X. Ver @see dataManagers.js para mais informações.
 */

/**
 * Opções do e-charts
 * @typedef {Object} EChartsOptions
 * @see {@link https://ecomfe.github.io/echarts-doc/public/en/option.html} Ver documentação do
 * e-Charts
 */

export default {
  /**
   * Gera as opções de configuração do e-charts para um gráfico de linhas com eixo horizontal de
   * tempo.
   * Caso desejar, é possível alterar o retorno desse gerador para aplicar outras customizações
   * não implementadas aqui.
   * @param {ChartValue} chartData Dados do gráfico.
   * @param {GenerateOptions} options Opções de configuração.
   * @returns {EChartsOptions} Opções de configuração do e-charts.
   */
  generate(chartData, options = {}) {
    const xDataManager = getDataManager(options?.xDataManager);
    const { xAxis, yAxis, series } = getSeriesAndAxis(chartData, xDataManager, options);

    const optionsResult = {
      title: { ...options?.title },
      color: getColorsListByLength(options?.legendAmount, options?.colorsList),
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: options?.tooltipFormatter,
        textStyle: {
          color: 'white',
        },
        backgroundColor: 'rgba(110, 110, 110, 0.85)',
        extraCssText: 'max-width: 80%; white-space: normal; word-break: break-word;',
        axisPointer: {
          type: 'cross',
          label: {
            color: themeService.getStyleVar('color-page-background'),
            formatter: tooltipLabelFormatter,
          },
        },
      },
      legend: {
        show: false,
      },
      xAxis,
      yAxis,
      series,
      grid: {
        right: getGridRight(),
        left: getGridLeft(options),
        top: 70,
        bottom: 45,
      },
      dataZoom: [
        {
          id: 'dataZoomX',
          show: false,
          realtime: true,
          type: 'slider',
          xAxisIndex: [0],
          filterMode: 'filter',
        },
        {
          id: 'dataZoomY',
          show: false,
          realtime: true,
          type: 'slider',
          yAxisIndex: [0],
          filterMode: 'empty',
        },
      ],
      ...getExtraOptions(options),
    };
    clearAxisInfo();
    return optionsResult;
  },
};
